import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

import Button from '../../components/ui/Button';
import WelcomeStep from './components/WelcomeStep';
import UserTypeStep from './components/UserTypeStep';
import FeatureDemoStep from './components/FeatureDemoStep';
import PersonalizationStep from './components/PersonalizationStep';
import CompletionStep from './components/CompletionStep';
import { logger } from '../../utils/logger';

const InteractiveOnboardingWizard = () => {
  const navigate = useNavigate();
  const { user, updateProfile } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [onboardingData, setOnboardingData] = useState({
    userType: '',
    notifications: {
      dailyReminders: true,
      reminderTime: '09:00',
      emailNotifications: true,
      pushNotifications: true
    },
    privacy: {
      shareWithCaregivers: false,
      anonymousAnalytics: true
    },
    preferences: {
      theme: 'auto',
      language: 'en'
    }
  });
  const [loading, setLoading] = useState(false);
  const [canSkip, setCanSkip] = useState(true);

  const totalSteps = 5;

  useEffect(() => {
    // Mark that user has seen the interactive wizard
    localStorage.setItem('hasSeenInteractiveWizard', 'true');
    
    // Check if user has already completed full onboarding
    const hasCompleted = localStorage.getItem('hasCompletedOnboarding');
    if (hasCompleted === 'true') {
      navigate('/daily-check-in');
    }
  }, [navigate]);

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    // Mark wizard as seen and redirect to user type selection
    localStorage.setItem('hasSeenInteractiveWizard', 'true');
    logger?.audit('ONBOARDING_SKIPPED', user?.id);
    navigate('/user-type-selection');
  };

  const handleDataUpdate = (field, value) => {
    setOnboardingData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleComplete = async () => {
    try {
      setLoading(true);

      // Save user preferences to profile
      await updateProfile({
        user_type: onboardingData?.userType,
        onboarding_completed: true
      });

      // Save to localStorage
      localStorage.setItem('userType', onboardingData?.userType);
      localStorage.setItem('hasSeenInteractiveWizard', 'true');

      logger?.audit('ONBOARDING_COMPLETED', user?.id, {
        userType: onboardingData?.userType
      });

      // Navigate to user type selection for final confirmation
      navigate('/user-type-selection');
    } catch (error) {
      logger?.error('Onboarding completion failed', error, { userId: user?.id });
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <WelcomeStep onNext={handleNext} />;
      case 2:
        return (
          <UserTypeStep
            selectedType={onboardingData?.userType}
            onSelect={(type) => handleDataUpdate('userType', type)}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 3:
        return (
          <FeatureDemoStep
            userType={onboardingData?.userType}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 4:
        return (
          <PersonalizationStep
            data={onboardingData}
            onUpdate={handleDataUpdate}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 5:
        return (
          <CompletionStep
            data={onboardingData}
            onComplete={handleComplete}
            onBack={handleBack}
            loading={loading}
          />
        );
      default:
        return null;
    }
  };

  const progressPercentage = (currentStep / totalSteps) * 100;

  return (
    <>
      <Helmet>
        <title>Welcome to MyBodyToday - Interactive Setup</title>
        <meta name="description" content="Get started with MyBodyToday through our guided setup" />
      </Helmet>
      <div className="min-h-screen bg-background flex flex-col">
        {/* Header with Progress */}
        <div className="bg-card border-b border-border sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <img src="/assets/images/MyBodyToday_logo_design2-removebg-preview1-1770673381636.png" alt="MyBodyToday Logo" className="w-8 h-8 object-contain" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-foreground">MyBodyToday Setup</h1>
                  <p className="text-xs text-muted-foreground">Step {currentStep} of {totalSteps}</p>
                </div>
              </div>
              {canSkip && currentStep < totalSteps && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSkip}
                >
                  Skip Setup
                </Button>
              )}
            </div>
            {/* Progress Bar */}
            <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
              <div
                className="bg-primary h-full transition-all duration-300 ease-out"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
        </div>

        {/* Step Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-4 py-8">
            {renderStep()}
          </div>
        </div>

        {/* Step Indicators */}
        <div className="bg-card border-t border-border py-4">
          <div className="max-w-4xl mx-auto px-4">
            <div className="flex items-center justify-center gap-2">
              {[...Array(totalSteps)]?.map((_, idx) => (
                <div
                  key={idx}
                  className={`h-2 rounded-full transition-all ${
                    idx + 1 === currentStep
                      ? 'w-8 bg-primary'
                      : idx + 1 < currentStep
                      ? 'w-2 bg-primary' :'w-2 bg-muted'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InteractiveOnboardingWizard;